import { useState } from 'react'
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Eye, EyeOff } from 'lucide-react'
import { Button, Input, Label } from '@/components/ui'
import { SocialLoginButtons, Divider } from '@/components/auth'
import { useAuth } from '@/hooks'
import { loginSchema, type LoginFormData } from '@/lib/validations'

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const {
    signIn,
    signInWithGoogle,
    signInWithFacebook,
    signInWithApple,
    isLoading,
    error,
    clearError,
  } = useAuth()
  const [showPassword, setShowPassword] = useState(false)

  // Support redirect from query params (for invite flow) or location state
  const redirectParam = searchParams.get('redirect')
  const previous = (location.state as { from?: { pathname: string; search?: string; hash?: string } })?.from
  const destination = redirectParam || (previous ? previous.pathname + (previous.search ?? '') + (previous.hash ?? '') : '/')
  const from = destination.startsWith('/') && !destination.startsWith('//') && !destination.includes('\\') ? destination : '/'

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginFormData) => {
    clearError()
    try {
      await signIn(data.email, data.password)
      navigate(from, { replace: true })
    } catch {
      // Error is handled by useAuth
    }
  }

  const handleSocialLogin = async (provider: 'google' | 'facebook' | 'apple') => {
    clearError()
    try {
      if (provider === 'google') await signInWithGoogle()
      else if (provider === 'facebook') await signInWithFacebook()
      else if (provider === 'apple') await signInWithApple()
      navigate(from, { replace: true })
    } catch {
      // Error is handled by useAuth
    }
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-serif font-semibold text-gray-900">Welcome to FamSteady</h2>
        <p className="mt-2 text-sm text-gray-600">
          Sign in to look out for the people you love.
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-error-50 p-4">
          <p className="text-sm text-error-700">{error}</p>
        </div>
      )}

      <SocialLoginButtons
        onGoogleClick={() => handleSocialLogin('google')}
        onFacebookClick={() => handleSocialLogin('facebook')}
        onAppleClick={() => handleSocialLogin('apple')}
        disabled={isLoading}
      />

      <Divider text="or continue with email" />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <Label htmlFor="email">Email address</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            className="mt-1"
            {...register('email')}
          />
          {errors.email && <p className="mt-1 text-sm text-error-600">{errors.email.message}</p>}
        </div>

        <div>
          <Label htmlFor="password">Password</Label>
          <div className="relative mt-1">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••"
              className="pr-10"
              {...register('password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1 text-sm text-error-600">{errors.password.message}</p>
          )}
        </div>

        <div className="flex items-center justify-end">
          <Link to="/forgot-password" className="text-sm text-primary-600 hover:text-primary-500">
            Forgot your password?
          </Link>
        </div>

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? 'Signing in...' : 'Sign in'}
        </Button>
      </form>

      <p className="text-center text-sm text-gray-600">
        Don&apos;t have an account?{' '}
        <Link
          to={redirectParam ? `/signup?redirect=${encodeURIComponent(redirectParam)}` : '/signup'}
          className="font-medium text-primary-600 hover:text-primary-500"
        >
          Sign up
        </Link>
      </p>
    </div>
  )
}
